import type { TestClock } from './core/clock';
import { FIXED_STEP_MS, type ManualFrameSource } from './core/loop';

/**
 * The verification surface for Playwright (BRIEF <phase_1_setup> item 5).
 *
 * Installed ONLY when the page is loaded with `?harness=1`. Without that flag this module
 * installs nothing and the game runs on `RealClock` + `requestAnimationFrame`. The harness owns
 * the injected clock and PRNG, so no test ever depends on real elapsed time or unseeded
 * randomness.
 */
export interface Harness {
  /** Advance the injected clock AND run the fixed-timestep updates that elapsed time implies. */
  advance(ms: number): void;
  /**
   * Jump the clock without simulating the gap. For "it is now three days later" scheduler cases.
   * Deliberately does not fast-forward the round timer.
   */
  setNow(epochMs: number): void;
  /** Reseed the PRNG. Rebuilds the app so the new stream governs from boot. */
  seed(n: number): void;
  /** The injected clock, so a test can jump relative to it. Never published to the page otherwise. */
  now(): number;
  /** Run exactly one frame: drain the accumulator, render, republish `window.__gameState`. */
  pump(): void;
  /** Total fixed updates run since boot. */
  ticks(): number;
  /** Clear persisted state and return to a freshly-booted entry card. */
  reset(): Promise<void>;
  /**
   * Mark the one-off conversion demonstration as already seen, without playing it (D-252).
   *
   * A test affordance, and it earns the exception. The demonstration sits between the entry card
   * and the first run of a fresh store, so every one of the ~1,400 tests that starts a run would
   * otherwise have to tap through it. Doing that for real is not free: leaving the demonstration
   * enters the run it delayed, and ending that run banks a `SessionRecord` — which is how the first
   * version of this broke the tests that count sessions.
   *
   * The real path is not bypassed anywhere it matters: `tests/e2e/demostracion.spec.ts` boots with
   * `keepDemo` and plays it exactly as a learner would.
   */
  skipDemo(): Promise<void>;
  /**
   * Every illegal state transition the machine has refused since boot (T45).
   *
   * The machine ignores an illegal transition rather than throwing, because killing a run over a
   * stray event costs the learner their remaining minutes. That makes the refusals invisible unless
   * something looks — this is what looks, and the E2E suite asserts the list is empty.
   */
  illegalTransitions(): { from: { phase: string; runPhase: string }; event: string }[];
}

export interface HarnessTarget {
  clock: TestClock;
  frames: ManualFrameSource;
  ticks(): number;
  reset(): Promise<void>;
  reseed(n: number): void;
  skipDemo(): Promise<void>;
  illegalTransitions(): { from: { phase: string; runPhase: string }; event: string }[];
}

declare global {
  interface Window {
    __harness?: Harness;
  }
}

/**
 * The loop simulates at most 8 fixed steps per frame (spiral-of-death guard), so a large advance
 * is delivered as a sequence of frames rather than one impossible one. This keeps harness time
 * behaving exactly like real time, only faster.
 */
const MAX_MS_PER_FRAME = FIXED_STEP_MS * 8;

export function installHarness(target: HarnessTarget, win: Window = window): Harness {
  const harness: Harness = {
    advance(ms: number): void {
      if (ms <= 0) {
        target.frames.pump();
        return;
      }
      // Step toward an absolute target rather than summing deltas, so a long advance lands on
      // exactly `start + ms` instead of accumulating float error across hundreds of frames.
      const end = target.clock.now() + ms;
      while (target.clock.now() < end) {
        target.clock.setNow(Math.min(end, target.clock.now() + MAX_MS_PER_FRAME));
        target.frames.pump();
      }
    },
    setNow(epochMs: number): void {
      target.clock.setNow(epochMs);
      target.frames.pump();
    },
    now(): number {
      return target.clock.now();
    },
    seed(n: number): void {
      target.reseed(n);
    },
    skipDemo(): Promise<void> {
      return target.skipDemo();
    },
    pump(): void {
      target.frames.pump();
    },
    ticks(): number {
      return target.ticks();
    },
    async reset(): Promise<void> {
      // Awaited: reset wipes the store and re-runs the whole boot sequence, so a test that read
      // state straight afterwards would otherwise be racing a half-seeded library.
      await target.reset();
      target.frames.pump();
    },
    illegalTransitions() {
      return target.illegalTransitions();
    },
  };

  win.__harness = harness;
  return harness;
}
